import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, Trash2, Filter, RefreshCw } from 'lucide-react';
import { useAdmin } from '@/hooks/useAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  backend_5xx: { label: 'Backend 5xx', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  backend_4xx: { label: 'Backend 4xx', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  frontend_js: { label: 'Frontend JS', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  frontend_api_fail: { label: 'API fail (FE)', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
};

export default function AdminErrorLog() {
  const { authFetch } = useAdmin();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeWindow, setRangeWindow] = useState('7d');
  const [kind, setKind] = useState('');
  const [statusMin, setStatusMin] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ window: rangeWindow, page: String(page), page_size: '50' });
      if (kind) qs.set('kind', kind);
      if (statusMin) qs.set('status_min', statusMin);
      if (q.trim()) qs.set('q', q.trim());
      const r = await authFetch(`${API}/admin/error-log?${qs}`);
      if (r?.ok) {
        const d = await r.json();
        setRows(d.rows || []);
        setTotal(d.total || 0);
        setSummary(d.summary || null);
      } else { setRows([]); setTotal(0); setSummary(null); }
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [authFetch, rangeWindow, kind, statusMin, q, page]);

  useEffect(() => { load(); }, [load]);

  async function deleteRow(id) {
    try {
      await authFetch(`${API}/admin/error-log/${id}`, { method: 'DELETE' });
      load();
    } catch { /* noop */ }
  }

  async function clearAll() {
    if (!window.confirm('Obrisati SVE zabilježene greške?')) return;
    try {
      const r = await authFetch(`${API}/admin/error-log/clear`, { method: 'POST' });
      if (r?.ok) { toast.success('Obrisano'); load(); }
    } catch (e) { toast.error(e.message); }
  }

  return (
    <div className="p-4 md:p-6" data-testid="admin-error-log-page">
      <header className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">Dnevnik grešaka</h1>
          <p className="text-sm text-gray-500">Centralni log svih 4xx/5xx grešaka backenda i JS exceptiona na frontendu.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={load} className="p-2 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-500" title="Osvježi" data-testid="error-log-refresh"><RefreshCw size={14} /></button>
          <button onClick={clearAll} className="px-3 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700" data-testid="error-log-clear">Obriši sve</button>
        </div>
      </header>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          {[
            ['Ukupno', summary.total, 'bg-gray-100 text-gray-800'],
            ['5xx', summary.backend_5xx, 'bg-red-100 text-red-700'],
            ['4xx', summary.backend_4xx, 'bg-amber-100 text-amber-800'],
            ['Frontend JS', summary.frontend_js, 'bg-purple-100 text-purple-700'],
            ['API fail (FE)', summary.frontend_api_fail, 'bg-blue-100 text-blue-700'],
          ].map(([k, v, cls]) => (
            <div key={k} className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-3" data-testid={`error-log-summary-${k.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="text-xs text-gray-500">{k}</div>
              <div className={`text-2xl font-bold mt-1 inline-block px-2 rounded ${cls}`}>{v ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Filter size={14} className="text-gray-400" />
        <select value={rangeWindow} onChange={e => { setPage(1); setRangeWindow(e.target.value); }}
          className="px-2 py-1.5 text-sm bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg" data-testid="error-log-window">
          <option value="1d">1 dan</option>
          <option value="7d">7 dana</option>
          <option value="30d">30 dana</option>
          <option value="90d">90 dana</option>
        </select>
        <select value={kind} onChange={e => { setPage(1); setKind(e.target.value); }}
          className="px-2 py-1.5 text-sm bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg" data-testid="error-log-kind">
          <option value="">Svi tipovi</option>
          <option value="backend_5xx">Backend 5xx</option>
          <option value="backend_4xx">Backend 4xx</option>
          <option value="frontend_js">Frontend JS</option>
          <option value="frontend_api_fail">API fail (FE)</option>
        </select>
        <input value={statusMin} onChange={e => setStatusMin(e.target.value)} placeholder="Min status (npr 500)" type="number"
          className="px-2 py-1.5 text-sm bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg w-36" data-testid="error-log-status-min" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Pretraži poruku…"
          className="px-2 py-1.5 text-sm bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg flex-1 min-w-[200px]" data-testid="error-log-search" />
      </div>

      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-slate-800 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Vrijeme</th>
                <th className="text-left px-3 py-2">Tip</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Ruta</th>
                <th className="text-left px-3 py-2">Poruka</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-8 text-gray-400"><Loader2 className="inline animate-spin mr-2" size={14} />Učitavanje…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-8 text-gray-400">Nema zabilježenih grešaka u ovom razdoblju.</td></tr>
              ) : rows.map(r => {
                const meta = KIND_META[r.kind] || { label: r.kind, color: 'bg-gray-100 text-gray-700' };
                return (
                  <tr key={r.id} className="border-t border-gray-100 dark:border-slate-800 align-top" data-testid={`error-log-row-${r.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleString('hr-HR') : '—'}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[11px] rounded ${meta.color}`}>{meta.label}</span></td>
                    <td className="px-3 py-2 font-mono text-xs">{r.status_code ?? '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs max-w-[280px] truncate" title={r.route}>{r.method} {r.route}</td>
                    <td className="px-3 py-2 text-xs">
                      <div className="max-w-[420px] truncate" title={r.message}>{r.message}</div>
                      {r.stack && <details className="text-[11px] text-gray-500 mt-1"><summary className="cursor-pointer">Stack</summary><pre className="whitespace-pre-wrap mt-1 max-h-40 overflow-auto">{r.stack}</pre></details>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => deleteRow(r.id)} className="text-gray-400 hover:text-red-600 p-1" data-testid={`error-log-delete-${r.id}`}><Trash2 size={12} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {total > 50 && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 dark:border-slate-800 text-xs text-gray-500">
            <span>Stranica {page} · ukupno {total}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-2 py-1 border rounded disabled:opacity-50">‹</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page * 50 >= total} className="px-2 py-1 border rounded disabled:opacity-50">›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
